// PackageViewer — office-side "View report". When the stored report PDF is ready it is shown directly in an
// iframe (the browser's own viewer gives save / print / download — no app buttons needed). For legacy / kill-
// switch / not-yet-prepared jobs it falls back to the old continuous raster preview (ReportDocument + appendix
// page images) with a single Save/Print action, so "View report" always works.
//
// This component intentionally exposes NO report-preparation controls (no generate / regenerate / package
// status / version). Preparation is the system's job (auto-prepared and self-healed in JobDetail); the office
// just views the report. Retry-on-failure lives on the report tab, office-only.
import React, { useEffect, useRef, useState } from 'react'
import { supabase } from '../lib/supabase'
import ReportDocument from './ReportDocument'
import { rasterisePdf, buildAppendixList, appendixPrintHtml, resolveSnapshotMedia } from '../lib/reportAppendix'
import { toast } from '../lib/toast'

export default function PackageViewer({ jobId, job, client, snapshot: snapshotProp, onClose }) {
  const [loading, setLoading] = useState(!snapshotProp)
  const [snap, setSnap] = useState(snapshotProp || null)
  const [ack, setAck] = useState(null)
  const [logos, setLogos] = useState({})
  const [media, setMedia] = useState({})              // storage_path → signed URL (RCM photos embed inline)
  const [mediaReady, setMediaReady] = useState(false) // gate print until inline media has resolved
  const [templateItems, setTemplateItems] = useState(null)
  const [checklistRev, setChecklistRev] = useState(null)  // bound checklist version (snapshot, else resolved live)
  const [appendices, setAppendices] = useState([])    // [{label, name, url, pages, status}] — ENDORSED (snapshot)
  const [extraFiles, setExtraFiles] = useState([])    // live appendix files NOT in the snapshot (office-only, non-print)
  const [storedUrl, setStoredUrl] = useState(null)    // signed URL of the stored report PDF (iframe view)
  const reportRef = useRef(null)

  useEffect(() => {
    (async () => {
      let s = snapshotProp
      if (!s) {
        const { data } = await supabase.from('audit_report_snapshots').select('*')
          .eq('job_id', jobId).order('version', { ascending: false }).limit(1)
        s = data?.[0] || null
        setSnap(s); setLoading(false)
      }
      const { data: a } = await supabase.from('report_ack_responses').select('*')
        .eq('job_id', jobId).order('submitted_at', { ascending: false }).limit(1)
      setAck(a?.[0] || null)
      const { data: fs } = await supabase.from('firm_settings').select('firm_logo_base64, sac_logo_base64, stamp_base64, official_package_enabled').eq('id', 1).maybeSingle()
      const flagOn = fs?.official_package_enabled !== false

      // Stored report PDF ready → show it directly in an iframe; skip the whole raster build.
      if (flagOn && s?.package_status === 'ready' && s?.package_path) {
        const { data: signed } = await supabase.storage.from('report-packages').createSignedUrl(s.package_path, 1800)
        if (signed?.signedUrl) { setStoredUrl(signed.signedUrl); return }
        // sign failed → fall through to the raster fallback so View report still works
      }

      // ── Legacy / fallback raster preview (old jobs, kill switch off, or not-yet-prepared) ──
      setLogos({
        company: fs?.firm_logo_base64 || localStorage.getItem('firm_logo_url') || '',
        sac: fs?.sac_logo_base64 || localStorage.getItem('sac_logo_url') || '',
        stamp: fs?.stamp_base64 || localStorage.getItem('firm_stamp_url') || '',
      })
      const inner = s?.snapshot || {}
      if (inner.checklist_revision) setChecklistRev(inner.checklist_revision)
      else if (inner.checklist_template_id) {
        const { data: tpl } = await supabase.from('checklist_templates').select('revision').eq('id', inner.checklist_template_id).maybeSingle()
        setChecklistRev(tpl?.revision || null)
      }
      const { data: liveEv } = await supabase.from('audit_evidence_files').select('storage_path, evidence_type, file_name')
        .eq('job_id', jobId).is('archived_at', null)
      const m = await resolveSnapshotMedia(inner.evidence)
      setMedia(m); setMediaReady(true)
      if (!(inner.checklist || []).length && inner.checklist_template_id) {
        const { data: ti } = await supabase.from('checklist_template_items').select('id, clause_ref, section, section_order, item_text')
          .eq('template_id', inner.checklist_template_id).is('archived_at', null).order('item_order')
        setTemplateItems(ti || null)
      }
      const snapEvidence = inner.evidence || []
      const appx = buildAppendixList(snapEvidence)
      const snapPaths = new Set(snapEvidence.map(e => e.storage_path))
      setExtraFiles(buildAppendixList(liveEv).filter(a => !snapPaths.has(a.ev?.storage_path)))
      const resolved = appx.map(a => ({ label: a.label, name: a.ev.file_name || 'document.pdf', url: '', pages: [], status: 'loading' }))
      setAppendices(resolved)
      for (let i = 0; i < appx.length; i++) {
        const a = appx[i]
        const { data: u } = await supabase.storage.from('audit-evidence').createSignedUrl(a.ev.storage_path, 1800)
        let pages = [], status = 'error', url = u?.signedUrl || ''
        if (url) {
          const isPdf = /\.pdf($|\?)/i.test(a.ev.file_name || '') || /\.pdf($|\?)/i.test(url)
          if (isPdf) { try { pages = await rasterisePdf(url); status = 'ready' } catch (e) { status = 'error' } }
        }
        setAppendices(prev => prev.map((p, idx) => idx === i ? { ...p, url, pages, status } : p))
      }
    })()
  }, [jobId, snapshotProp])

  async function download(ev) {
    if (!ev.storage_path) return
    const { data } = await supabase.storage.from('audit-evidence').createSignedUrl(ev.storage_path, 120)
    if (data?.signedUrl) window.open(data.signedUrl, '_blank')
  }

  // Legacy fallback only: print the raster preview (report + appendix pages) as one continuous document.
  function printReport() {
    const html = reportRef.current?.innerHTML
    if (!html) return
    const w = window.open('', '_blank', 'width=900,height=1000')
    if (!w) { toast('Allow pop-ups to save the PDF', 'warning'); return }
    const appxHtml = appendixPrintHtml(appendices)
    w.document.write(`<!doctype html><html><head><title>Audit Report</title><meta charset="utf-8">
      <style>
        @page { margin: 14mm; }
        * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; box-sizing: border-box; }
        html, body { margin: 0; padding: 0; height: auto; overflow: visible; font-family: Arial, Helvetica, sans-serif; color: #1a1a1a; }
        img { max-width: 100%; }
        table { width: 100%; }
        .pkg-noprint { display: none !important; }
      </style></head><body>${html}${appxHtml}</body></html>`)
    w.document.close(); w.focus()
    setTimeout(() => { w.print() }, 500)
  }

  const currentVersion = snap?.version ?? null
  const anyLoading = appendices.some(a => a.status === 'loading')

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 880, maxHeight: '92vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
        <div className="modal-header" style={{ position: 'sticky', top: 0, background: 'var(--bg)', zIndex: 1 }}>
          <h2>Audit report</h2>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {/* Save / print only for the legacy raster fallback — the stored-PDF iframe has its own toolbar. */}
            {!storedUrl && !loading && snap && (
              <>
                {(anyLoading || !mediaReady) && <span style={{ fontSize: 11, color: 'var(--text3)' }}>{!mediaReady ? 'Loading photos…' : 'Preparing pages…'}</span>}
                <button className="sm" onClick={printReport} disabled={!snap || anyLoading || !mediaReady}>Save / print</button>
              </>
            )}
            <button onClick={onClose} style={{ border: 'none', background: 'none', fontSize: 18, cursor: 'pointer' }}>×</button>
          </div>
        </div>

        {storedUrl ? (
          <iframe src={storedUrl} title="Audit report" style={{ width: '100%', height: '82vh', border: 'none' }} />
        ) : loading ? <div style={{ padding: 24, color: 'var(--text3)' }}>Loading report…</div>
          : !snap ? <div style={{ padding: 24, color: 'var(--text3)', fontStyle: 'italic' }}>No submitted report found for this job.</div>
          : <>
              <div ref={reportRef} style={{ padding: '6px 6px 18px' }}>
                <ReportDocument doc={{ client, job, snapshot: snap, ack, currentVersion }} logos={logos} media={media} templateItems={templateItems} checklistRevision={checklistRev} onDownload={download} />
              </div>
              {/* On-screen appendix PREVIEW ONLY — OUTSIDE reportRef so it is NOT captured into the print. */}
              {appendices.map((a, i) => (
                <div key={i} className="pkg-noprint" style={{ borderTop: '2px solid #2b2b2b', marginTop: 18, paddingTop: 12 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.4 }}>{a.label}</div>
                  {a.status === 'loading' && <div style={{ fontSize: 12, color: 'var(--text3)' }}>Preparing pages…</div>}
                  {a.status === 'ready' && a.pages.map((p, j) => (
                    <img key={j} src={p.screen} alt={`${a.label} page ${j + 1}`} style={{ width: '100%', display: 'block', marginBottom: 10, border: '0.5px solid var(--border)' }} />
                  ))}
                  {a.status === 'error' && (
                    <div style={{ fontSize: 12.5, color: 'var(--text2)' }}>
                      <strong>{a.name}</strong> is attached{a.url && <> — <button className="sm pkg-noprint" onClick={() => window.open(a.url, '_blank')}>open</button></>}.
                      <span style={{ color: 'var(--text3)' }}> (Not a PDF — could not inline; append it manually when saving the final PDF.)</span>
                    </div>
                  )}
                </div>
              ))}
              {extraFiles.length > 0 && (
                <div className="pkg-noprint" style={{ borderTop: '2px dashed #b58900', marginTop: 18, paddingTop: 12 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4, color: '#8a6d00', textTransform: 'uppercase', letterSpacing: 0.4 }}>Additional current files — not part of the endorsed report</div>
                  <div style={{ fontSize: 11.5, color: 'var(--text3)', marginBottom: 8 }}>Uploaded/changed after this report was submitted, so they are not in the endorsed snapshot and are excluded from the official print. Re-submit the report to include them in a new version.</div>
                  {extraFiles.map((a, i) => (
                    <div key={i} style={{ fontSize: 12.5, color: 'var(--text2)', marginBottom: 4 }}>
                      <strong>{a.label}:</strong> {a.ev.file_name || 'file'} <button className="sm pkg-noprint" onClick={() => download(a.ev)}>open</button>
                    </div>
                  ))}
                </div>
              )}
            </>}
      </div>
    </div>
  )
}
